import { GraphQLClient } from 'graphql-request';
import isNil from 'lodash/isNil';

import { AUTHORIZER_TIMEOUT_MESSAGE, HTTP_STATUS_UNAUTHORIZED } from './errors';

const ENDPOINT =
  typeof window !== 'undefined'
    ? ((import.meta as { env?: { VITE_GRAPHQL_ENDPOINT?: string } }).env?.VITE_GRAPHQL_ENDPOINT ?? '')
    : (process.env.VITE_GRAPHQL_ENDPOINT ?? '');

let tokenGetter: (() => Promise<string | null>) | null = null;

export const setAuthTokenGetter = (getter: () => Promise<string | null>) => {
  tokenGetter = getter;
};

export const getAuthToken = async (): Promise<string | null> => {
  return tokenGetter ? await tokenGetter() : null;
};

export const hasAuthTokenGetter = (): boolean => tokenGetter !== null;

type AuthTokenRefresher = (deniedToken: string | null) => Promise<string | null>;

let tokenRefresher: AuthTokenRefresher | null = null;

export const setAuthTokenRefresher = (refresher: AuthTokenRefresher) => {
  tokenRefresher = refresher;
};

const BEARER_PREFIX = 'Bearer ';

const isRenewableAuthDenial = async (response: Response): Promise<boolean> => {
  if (response.status !== HTTP_STATUS_UNAUTHORIZED) {
    return false;
  }
  const body = await response
    .clone()
    .text()
    .catch(() => '');
  return !body.includes(AUTHORIZER_TIMEOUT_MESSAGE);
};

const readDeniedToken = (headers: Headers): string | null => {
  const authorization = headers.get('Authorization') ?? '';
  return authorization.startsWith(BEARER_PREFIX) ? authorization.slice(BEARER_PREFIX.length) : null;
};

// The authorizer rejects before any resolver runs, so a replayed mutation is never applied twice
export const fetchWithAuthTokenRenewal = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const response = await fetch(input, init);
  if (isNil(tokenRefresher) || !(await isRenewableAuthDenial(response))) {
    return response;
  }
  const headers = new Headers(init?.headers);
  const deniedToken = readDeniedToken(headers);
  const renewedToken = await tokenRefresher(deniedToken).catch(() => null);
  if (isNil(renewedToken) || renewedToken === deniedToken) {
    return response;
  }
  headers.set('Authorization', `${BEARER_PREFIX}${renewedToken}`);
  return fetch(input, { ...init, headers });
};

// Registered callbacks notified when the GraphQL authorizer rejects a request
// (HTTP 401 / UnauthorizedException). The frontend uses this to surface a
// user-facing "access revoked" screen — typically triggered when a landlord
// flips a tenant's `User.status` to DISABLED.
const unauthorizedHandlers = new Set<() => void>();

export const onUnauthorizedGraphqlRequest = (handler: () => void): (() => void) => {
  unauthorizedHandlers.add(handler);
  return () => {
    unauthorizedHandlers.delete(handler);
  };
};

const notifyUnauthorized = () => {
  unauthorizedHandlers.forEach((h) => {
    try {
      h();
    } catch (err) {
      console.error('[graphqlClient] unauthorized handler threw', err);
    }
  });
};

const sessionExpiredHandlers = new Set<() => void>();

export const onSessionExpiredGraphqlRequest = (handler: () => void): (() => void) => {
  sessionExpiredHandlers.add(handler);
  return () => {
    sessionExpiredHandlers.delete(handler);
  };
};

export const notifySessionExpired = () => {
  sessionExpiredHandlers.forEach((handler) => {
    try {
      handler();
    } catch (error) {
      console.error('[graphqlClient] session expired handler threw', error);
    }
  });
};

const notifyOnAuthRejection = (response: unknown) => {
  // `graphql-request` invokes responseMiddleware with either a successful
  // response object or a ClientError. We branch on the error shape and
  // treat HTTP 401s and AppSync `UnauthorizedException` as access-revoked
  // — but ONLY when the rejection is a real auth denial. AppSync also
  // returns 401 + UnauthorizedException when the authorizer Lambda times
  // out ("...because the lambda invocation timed out."), which is a
  // transient infra blip and must not trigger the Access Revoked screen.
  const error = response as {
    response?: { status?: number; errors?: { errorType?: string; message?: string }[] };
  };
  const status = error.response?.status;
  const firstError = error.response?.errors?.[0];
  const errorType = firstError?.errorType;
  const message = firstError?.message ?? '';
  const isAuthRejection = status === HTTP_STATUS_UNAUTHORIZED || errorType === 'UnauthorizedException';
  if (!isAuthRejection) {
    return;
  }
  const isLambdaTimeout = message.includes(AUTHORIZER_TIMEOUT_MESSAGE);
  if (isLambdaTimeout) {
    console.warn('[graphqlClient] authorizer timed out — treating as transient, not access-revoked');
    return;
  }
  notifyUnauthorized();
};

interface GraphqlClientOptions {
  /** False for an API whose 401 means "this call is not allowed" rather than "your access was revoked". */
  reportsAccessRevocation?: boolean;
}

export const createGraphqlClient = (
  endpoint: string,
  { reportsAccessRevocation = true }: GraphqlClientOptions = {}
): GraphQLClient =>
  new GraphQLClient(endpoint, {
    fetch: fetchWithAuthTokenRenewal,
    requestMiddleware: async (request) => {
      const token = await getAuthToken();
      if (tokenGetter && !token) {
        notifySessionExpired();
        throw new Error('[graphqlClient] no auth token available — session expired or auth server unreachable');
      }
      // graphql-request hands over a Headers instance: spreading it would drop Content-Type and Accept.
      const headers = new Headers(request.headers);
      headers.set('Authorization', token ? `Bearer ${token}` : '');
      return { ...request, headers };
    },
    responseMiddleware: reportsAccessRevocation ? notifyOnAuthRejection : undefined,
  });

export const graphqlClient = createGraphqlClient(ENDPOINT);

export const setGraphqlEndpoint = (endpoint: string): void => {
  graphqlClient.setEndpoint(endpoint);
};
